export type LiveUpdateMode = "inactive" | "owner" | "follower" | "per-tab";

export type LiveUpdateSharingSupport =
  "unknown" | "supported" | "no-locks" | "no-broadcast-channel" | "lock-error";

export const LIVE_UPDATE_FAILURE_CATEGORIES = [
  "authentication",
  "configuration",
  "invalid-message",
  "network",
  "refresh",
  "token",
] as const;

export type LiveUpdateFailureCategory =
  (typeof LIVE_UPDATE_FAILURE_CATEGORIES)[number];

export type LiveUpdateState =
  "disabled" | "idle" | "connecting" | "connected" | "delayed" | "unavailable";

export interface LiveUpdateCoordination {
  mode: LiveUpdateMode;
  sharingSupport: LiveUpdateSharingSupport;
  tabId: string; // Correlates tabs during multi-tab testing and it's created rabdomly per coordinator
  peerCount: number | null; // Other tabs this owner knows about. Null unless this tab owns the socket.
}

export interface LiveUpdateStateTransition {
  at: number;
  state: LiveUpdateState;
  failureCategory: LiveUpdateFailureCategory | null;
}

export interface LiveUpdateEventRecord {
  at: number;
  type: string;
  publishedAt: number;
  outcome: "accepted" | "ignored";
  summaryCount?: number;
}

export interface LiveUpdateSubscriptionDiagnostics {
  subscriptionId: number;
  acceptedEventCount: number;
  refreshStartedCount: number;
  refreshCompletedCount: number;
  refreshFailedCount: number;
  lastRefreshAt: number | null;
  lastRefreshDurationMs: number | null;
}

export interface LiveUpdateDiagnostics {
  state: LiveUpdateState;
  coordination: LiveUpdateCoordination;
  becameOwnerCount: number;
  fellBackCount: number;
  retryAttempt: number;
  tokenExpiresAt: number | null;
  receivedEventCount: number;
  acceptedEventCount: number;
  ignoredEventCount: number;
  malformedEventCount: number;
  refreshStartedCount: number;
  refreshCompletedCount: number;
  refreshFailedCount: number;
  activeRefreshCount: number;
  lastEventAt: number | null;
  lastRefreshAt: number | null;
  lastFailureCategory: LiveUpdateFailureCategory | null;
  subscriptions: readonly LiveUpdateSubscriptionDiagnostics[];
  transitions: readonly LiveUpdateStateTransition[]; // Oldest first, capped at LIVE_UPDATE_DIAGNOSTICS_LOG_LIMIT
  events: readonly LiveUpdateEventRecord[];
}

export const LIVE_UPDATE_DIAGNOSTICS_LOG_LIMIT = 50;

export const createInitialCoordination = (): LiveUpdateCoordination => ({
  mode: "inactive",
  sharingSupport: "unknown",
  tabId: "",
  peerCount: null,
});

export const createInitialDiagnostics = (
  enabled: boolean,
): LiveUpdateDiagnostics => ({
  state: enabled ? "idle" : "disabled",
  coordination: createInitialCoordination(),
  becameOwnerCount: 0,
  fellBackCount: 0,
  retryAttempt: 0,
  tokenExpiresAt: null,
  receivedEventCount: 0,
  acceptedEventCount: 0,
  ignoredEventCount: 0,
  malformedEventCount: 0,
  refreshStartedCount: 0,
  refreshCompletedCount: 0,
  refreshFailedCount: 0,
  activeRefreshCount: 0,
  lastEventAt: null,
  lastRefreshAt: null,
  lastFailureCategory: null,
  subscriptions: [],
  transitions: [],
  events: [],
});

export const createSubscriptionDiagnostics = (
  subscriptionId: number,
): LiveUpdateSubscriptionDiagnostics => ({
  subscriptionId,
  acceptedEventCount: 0,
  refreshStartedCount: 0,
  refreshCompletedCount: 0,
  refreshFailedCount: 0,
  lastRefreshAt: null,
  lastRefreshDurationMs: null,
});

export const appendLogEntry = <T>(
  log: readonly T[],
  entry: T,
  limit = LIVE_UPDATE_DIAGNOSTICS_LOG_LIMIT,
): readonly T[] =>
  log.length < limit
    ? [...log, entry]
    : [...log.slice(log.length - limit + 1), entry];

export interface DiagnosticsReportContext {
  generatedAt: number;
}

export const sanitiseLiveUpdateDiagnostics = (
  diagnostics: LiveUpdateDiagnostics,
  context: DiagnosticsReportContext,
): string =>
  JSON.stringify(
    {
      version: 2,
      generatedAt: context.generatedAt,
      state: diagnostics.state,
      coordination: {
        mode: diagnostics.coordination.mode,
        sharingSupport: diagnostics.coordination.sharingSupport,
        peerCount: diagnostics.coordination.peerCount,
      },
      becameOwnerCount: diagnostics.becameOwnerCount,
      fellBackCount: diagnostics.fellBackCount,
      retryAttempt: diagnostics.retryAttempt,
      tokenExpiresAt: diagnostics.tokenExpiresAt,
      receivedEventCount: diagnostics.receivedEventCount,
      acceptedEventCount: diagnostics.acceptedEventCount,
      ignoredEventCount: diagnostics.ignoredEventCount,
      malformedEventCount: diagnostics.malformedEventCount,
      refreshStartedCount: diagnostics.refreshStartedCount,
      refreshCompletedCount: diagnostics.refreshCompletedCount,
      refreshFailedCount: diagnostics.refreshFailedCount,
      activeRefreshCount: diagnostics.activeRefreshCount,
      lastEventAt: diagnostics.lastEventAt,
      lastRefreshAt: diagnostics.lastRefreshAt,
      lastFailureCategory: diagnostics.lastFailureCategory,
      subscriptions: diagnostics.subscriptions.map((subscription) => ({
        subscriptionId: subscription.subscriptionId,
        acceptedEventCount: subscription.acceptedEventCount,
        refreshStartedCount: subscription.refreshStartedCount,
        refreshCompletedCount: subscription.refreshCompletedCount,
        refreshFailedCount: subscription.refreshFailedCount,
        lastRefreshAt: subscription.lastRefreshAt,
        lastRefreshDurationMs: subscription.lastRefreshDurationMs,
      })),
      transitions: diagnostics.transitions.map((transition) => ({
        at: transition.at,
        state: transition.state,
        failureCategory: transition.failureCategory,
      })),
      events: diagnostics.events.map((event) => ({
        at: event.at,
        type: event.type,
        publishedAt: event.publishedAt,
        outcome: event.outcome,
        ...(event.summaryCount === undefined
          ? {}
          : { summaryCount: event.summaryCount }),
      })),
    },
    null,
    2,
  );
